<!-- src/components/StraitMap.svelte
     v10 step 4 — 純 4 個 API 版本(applyStep 相容層已移除)
     決策依據:
       - 決策 A:onMount 用 straitSceneInfo.update(() => container) 註冊方法物件
       - 決策 B:#full-box-bg 寫死在這個元件裡,跟日本原版位置一致
       - 路線 2 + 微強化:跨海峽切換用淡入淡出(v9 機制),scene 內部用 D3 transition 變焦
       - step 3d:加上 #intro-curtain 黑布,給揭幕儀式
-->
<script>
  import { onMount } from 'svelte'
  import * as topojson from 'topojson'
  import * as d3 from 'd3'
  import { straitSceneInfo, currentSceneId } from '../stores/SceneDataStore.js'
  import { isMobile } from '../stores/DeviceDetectorStore.js'
  import { straitScenes, sceneOrder, MAP_PRESETS, getSceneBbox } from '../data/straitScenes.js'

  const width = $isMobile ? window.innerWidth : document.body.clientWidth - 15
  const height = window.innerHeight

  const FLY_DURATION_MS = 800 // flyToBbox 的 transition 長度

  let svg, defs
  const landCache = new Map()
  const layerDataCache = new Map()
  const sceneStates = {}

  onMount(() => {
    svg = d3.select('#strait-map')
    defs = svg.append('defs')

    sceneOrder.forEach(sceneId => {
      const scene = straitScenes[sceneId]
      const g = svg.append('g')
        .attr('class', 'scene-group')
        .attr('id', `scene-${sceneId}`)
        .style('opacity', 0)
        .style('pointer-events', 'none')
      // sceneStates 結構升級:多 projection / currentBbox / landFeatures / layerFeatures
      sceneStates[sceneId] = {
        built: false,
        g,
        scene,
        projection: null,    // 當前 projection (flyToBbox 會更新)
        currentBbox: null,   // 當前顯示的 bbox(flyToBbox 後會跟著變)
        landFeatures: null,  // GeoJSON features,用於重畫
        layerFeatures: {},   // { layerId: features[] }
      }
    })

    buildScene(sceneOrder[0])

    // ⭐ 決策 A:把四個方法註冊進 store
    straitSceneInfo.update(() => ({
      flyToBbox,
      setVisibleLayers,
      setActiveStraitId,
      interpolateBbox,
    }))
  })

  // ============================================================
  // GeoJSON 載入(沿用 v9)
  // ============================================================
  async function loadLand(url) {
    if (landCache.has(url)) return landCache.get(url)
    const promise = d3.json(url).then(topo => {
      const key = topo.objects.countries ? 'countries' : Object.keys(topo.objects)[0]
      return topojson.feature(topo, topo.objects[key])
    })
    landCache.set(url, promise)
    return promise
  }

  async function resolveLayerData(data) {
    if (Array.isArray(data)) return data
    if (typeof data === 'string') {
      if (layerDataCache.has(data)) return layerDataCache.get(data)
      const promise = d3.json(data).then(geo => geo.features || geo)
      layerDataCache.set(data, promise)
      return promise
    }
    return []
  }

  // ============================================================
  // [v13] normalizeFeatures(features, sceneId, layer?)
  //   消化 QGIS 來的 GeoJSON,讓它符合 drawLayer 的期望:
  //     1. visible_in 過濾  ← 同份 GeoJSON 跨 scene 共用的關鍵機制
  //     2. Name → name      ← QGIS 屬性表大小寫不一致時的相容
  //     3. 座標去 z         ← QGIS 預設 export 3D 座標,我們只要 2D
  //     4. featurePropertyOverrides ← 用 layer 上的對應表覆寫
  //          feature.properties。常見用途:QGIS export 的英文 name 在
  //          straitScenes.js 套上中文標註 + labelLng/labelLat。
  //          對應 key 為 feature 的原始 name 值 (Name 或 name)。
  //
  //   visible_in 規則:
  //     - 沒有 visible_in 欄位 → 一律顯示 (向後相容 inline data)
  //     - 有 visible_in → 用 "|" 切開,sceneId 在裡面才顯示
  // ============================================================
  function normalizeFeatures(features, sceneId, layer = null) {
    if (!Array.isArray(features)) return []
    const overrides = layer?.featurePropertyOverrides || null
    return features
      .filter(f => {
        const v = f.properties?.visible_in
        if (!v) return true
        if (Array.isArray(v)) return v.includes(sceneId)
        if (typeof v === 'string') {
          return v.split('|').map(s => s.trim()).includes(sceneId)
        }
        return true
      })
      .map(f => {
        const props = f.properties || {}
        // Name → name (QGIS 大寫相容); 原始 key 用於 featurePropertyOverrides 查表
        const originalKey = props.name ?? props.Name ?? ''
        const overrideForThis = overrides?.[originalKey] || {}
        // 去 z 座標
        let geom = f.geometry
        if (geom?.coordinates) {
          geom = { ...geom, coordinates: stripZ(geom.coordinates) }
        }
        return {
          ...f,
          properties: { ...props, name: originalKey, ...overrideForThis },
          geometry: geom,
        }
      })
  }

  // 遞迴去除座標的第三維 (Point / LineString / Polygon / MultiPolygon 都吃)
  function stripZ(coords) {
    if (typeof coords[0] === 'number') {
      return coords.length > 2 ? [coords[0], coords[1]] : coords
    }
    return coords.map(stripZ)
  }

  // ============================================================
  // bbox → projection(沿用 v9)
  // ============================================================
  function createProjectionForBbox(bbox, w, h, fillRatio = 1.0) {
    const [[west, south], [east, north]] = bbox
    const projection = d3.geoMercator()
      .center([(west + east) / 2, (south + north) / 2])
      .scale(1)
      .translate([w / 2, h / 2])
    const [x0, y1] = projection([west, north])
    const [x1, y0] = projection([east, south])
    const scale = fillRatio * Math.min(w / Math.abs(x1 - x0), h / Math.abs(y0 - y1))
    projection.scale(scale)
    return projection
  }

  // ============================================================
  // [v15] 桌機/手機分版 bbox
  //
  //   每個 scene 都有 bboxDesktop + bboxMobile,依 $isMobile 切換。
  //   helper 在 straitScenes.js 內,讓外部呼叫方 (StraitMapStepScroller,
  //   path_transition 處理) 也能用同一份邏輯。
  //
  //   $isMobile 變化只在初次 buildScene 時生效,因為 buildScene 結果
  //   會快取在 sceneStates。實務上使用者很少在閱讀中途 resize 視窗,
  //   故不處理 resize 即時切換。
  // ============================================================


  // ============================================================
  // buildScene:onMount 預先建好每個 scene(沿用 v9 + 補資料快取)
  // ============================================================
  async function buildScene(sceneId) {
    const state = sceneStates[sceneId]
    if (!state || state.built) return
    state.built = true

    const scene = state.scene
    const g = state.g
    const land = await loadLand(scene.landSource)

    // [v15] 用 getSceneBbox 取代 scene.bbox,依 $isMobile 選用 desktop/mobile
    const initialBbox = getSceneBbox(scene, $isMobile)
    const projection = createProjectionForBbox(initialBbox, width, height, 1.0)
    const path = d3.geoPath(projection)
    state.projection = projection
    state.currentBbox = initialBbox
    state.landFeatures = land

    // 注意:clip 範圍用「整個 SVG 的視窗大小」,不再卡死於初始 bbox
    // 這是路線 2+微強化的關鍵——bbox 會變,clip 不能跟著變
    defs.append('clipPath').attr('id', `clip-${sceneId}`)
      .append('rect').attr('x', 0).attr('y', 0).attr('width', width).attr('height', height)
    g.append('rect').attr('class', 'sea-bg')
      .attr('x', 0).attr('y', 0).attr('width', width).attr('height', height)

    const contentG = g.append('g')
      .attr('class', 'content-g')
      .attr('clip-path', `url(#clip-${sceneId})`)
    contentG.append('path').datum(land).attr('class', 'land').attr('d', path)

    if (scene.layers) {
      for (const layer of scene.layers) {
        const rawFeatures = await resolveLayerData(layer.data)
        // [v13] visible_in 過濾 + Name 大小寫相容 + 去 z 座標 + 屬性覆寫
        const features = normalizeFeatures(rawFeatures, sceneId, layer)
        state.layerFeatures[layer.id] = features
        // alwaysVisible 的 layer 起始 opacity = 1(不受 step 影響)
        const initialOpacity = layer.alwaysVisible ? 1 : 0
        drawLayer(contentG, layer, features, path, projection, initialOpacity)
      }
    }

    g.append('text').attr('class', 'scene-title')
      .attr('x', width / 2).attr('y', 40).text(scene.title)
  }

  // ============================================================
  // [v13] resolveLayerStyle(layer, feature?)
  //   三層覆蓋:
  //     1. MAP_PRESETS[layer.preset]      ← preset 預設值
  //     2. layer.styleOverride            ← layer 層級覆蓋(整層)
  //     3. feature?.properties            ← feature 層級覆蓋(特例)
  //
  //   向後相容:layer 沒有 preset 時 fallback 到 layer.style + layer.type
  //   方便其他尚未遷移的 scene 繼續用舊格式。
  //
  //   layer.type 的解析:
  //     - 新格式: 從 preset.type 拿
  //     - 舊格式: 直接讀 layer.type
  // ============================================================
  function resolveLayerStyle(layer, feature = null) {
    let merged
    if (layer.preset) {
      // 新格式 — preset 系統
      const preset = MAP_PRESETS[layer.preset]
      if (!preset) {
        console.warn(`[StraitMap] Unknown preset: ${layer.preset}`)
        return { type: 'point' }
      }
      merged = {
        ...preset,
        ...(layer.styleOverride || {}),
        ...(feature?.properties || {}),
      }
    } else {
      // 舊格式 — layer.style + layer.type
      merged = {
        type: layer.type,
        ...(layer.style || {}),
        ...(feature?.properties || {}),
      }
    }

    // [v13.4] RWD 字級:手機時 fontSize 改用 fontSizeMobile (若有定義)
    //   `fontSizeMobile` 可在 preset / styleOverride / properties 三層任一指定,
    //   合併後若存在就接管 fontSize。例如:
    //     countryLabel preset: { fontSize: 14, fontSizeMobile: 12 }
    //     第一島鏈 properties: { fontSize: 14, fontSizeMobile: 13 }
    if ($isMobile && merged.fontSizeMobile != null) {
      merged = { ...merged, fontSize: merged.fontSizeMobile }
    }
    return merged
  }

  // ============================================================
  // drawLayer:畫 polygon / line / point(沿用 v9, v13 加入 preset 支援)
  // ============================================================
  function drawLayer(parent, layer, features, path, projection, initialOpacity = 1) {
    const g = parent.append('g')
      .attr('class', `layer layer-${layer.id}`)
      .style('opacity', initialOpacity)

    // 以 layer 層級樣式判斷 type (feature 不影響 type)
    const layerStyle = resolveLayerStyle(layer)
    const type = layerStyle.type

    if (type === 'polygon') {
      g.selectAll('path').data(features).join('path')
        .attr('d', path)
        .attr('fill', f => resolveLayerStyle(layer, f).fill || 'none')
        .attr('fill-opacity', f => resolveLayerStyle(layer, f).fillOpacity ?? 1)
        .attr('stroke', f => resolveLayerStyle(layer, f).stroke || 'none')
        .attr('stroke-width', f => resolveLayerStyle(layer, f).strokeWidth || 1)
        .attr('stroke-dasharray', f => resolveLayerStyle(layer, f).strokeDasharray || null)
    } else if (type === 'line') {
      g.selectAll('path.line-geom').data(features).join('path')
        .attr('class', 'line-geom')
        .attr('d', path).attr('fill', 'none')
        .attr('stroke', f => resolveLayerStyle(layer, f).stroke || '#fff')
        .attr('stroke-width', f => resolveLayerStyle(layer, f).strokeWidth || 1)
        .attr('stroke-dasharray', f => resolveLayerStyle(layer, f).strokeDasharray || null)

      // 線的標注: properties.labelLng/labelLat 為標注位置 (新格式)
      //          或 style.labelAt: [lng, lat] (舊格式相容)
      const labeledLines = features
        .map(f => {
          const s = resolveLayerStyle(layer, f)
          if (!s.showLabel) return null
          const text = s.labelText || f.properties?.name || ''
          let pos = null
          if (s.labelLng != null && s.labelLat != null) {
            pos = projection([s.labelLng, s.labelLat])
          } else if (s.labelAt) {
            pos = projection(s.labelAt)
          }
          if (!pos || !text) return null
          return { pos, text, style: s }
        })
        .filter(Boolean)

      g.selectAll('text.line-label').data(labeledLines).join('text')
        .attr('class', 'line-label point-label')
        .attr('x', d => d.pos[0])
        .attr('y', d => d.pos[1])
        .attr('text-anchor', d => d.style.labelAnchor || 'middle')
        .style('fill', d => d.style.fontColor || d.style.stroke || '#fff')
        .style('font-size', d => `${d.style.fontSize || 12}px`)
        .style('font-weight', d => d.style.fontWeight || 400)
        .style('stroke', d => d.style.textStroke || null)
        .style('stroke-width', d => d.style.textStrokeWidth ? `${d.style.textStrokeWidth}px` : null)
        .text(d => d.text)
    } else if (type === 'point') {
      const pts = features.map(f => {
        const s = resolveLayerStyle(layer, f)
        const props = f.properties || {}
        return {
          coord: f.geometry.coordinates,
          name: props.name || '',
          // [v15.1] labelLng/labelLat 從 featurePropertyOverrides 進來,
          //   讓 point 標註位置可獨立於 geometry 座標 (line 類型 L300 早就支援)
          labelLng: props.labelLng,
          labelLat: props.labelLat,
          style: s,
        }
      })

      // 點符號:依 shape (新) / symbol (舊) 分流
      pts.forEach(p => {
        const s = p.style
        if (s.showDot === false) return // 純文字標注,不畫點

        const [cx, cy] = projection(p.coord)
        const shape = s.shape || s.symbol

        if (shape === 'diamond') {
          const r = s.radius || 6
          g.append('polygon')
            .attr('points', `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`)
            .attr('fill', s.fill || '#fff')
            .attr('stroke', s.stroke && s.stroke !== 'none' ? s.stroke : null)
            .attr('stroke-width', s.strokeWidth || null)
        } else if (shape === 'triangle') {
          const sym = d3.symbol().type(d3.symbolTriangle).size(80)
          g.append('path')
            .attr('d', sym())
            .attr('transform', `translate(${cx}, ${cy})`)
            .attr('fill', s.fill || '#fff')
            .attr('stroke', s.stroke && s.stroke !== 'none' ? s.stroke : null)
        } else {
          // circle (預設)
          g.append('circle')
            .attr('cx', cx)
            .attr('cy', cy)
            .attr('r', s.radius || 4)
            .attr('fill', s.fill || '#fff')
            .attr('stroke', s.stroke && s.stroke !== 'none' ? s.stroke : null)
            .attr('stroke-width', s.strokeWidth || null)
        }
      })

      // 文字標注:除非 showLabel === false,否則就畫
      pts.forEach(p => {
        const s = p.style
        if (s.showLabel === false) return
        // 沒有名字就不畫
        if (!p.name) return

        // [v15.1] 標註位置:優先用 labelLng/labelLat (來自 featurePropertyOverrides),
        //   沒有才用 feature geometry 座標。對比 line 類型 L300 早就支援。
        //   有 labelLng/labelLat 時 offset 不加 (位置已絕對指定);沒有時保持原 offset 邏輯
        const hasLabelCoord = p.labelLng != null && p.labelLat != null
        const [cx, cy] = hasLabelCoord
          ? projection([p.labelLng, p.labelLat])
          : projection(p.coord)
        const offX = hasLabelCoord ? 0 : (s.labelOffsetX ?? (s.showDot === false ? 0 : 9))
        const offY = hasLabelCoord ? 0 : (s.labelOffsetY ?? (s.showDot === false ? 0 : 4))

        // 自動 anchor:沒指定 anchor 時用 offsetX 正負號推斷
        let anchor = s.labelAnchor
        if (!anchor) {
          anchor = hasLabelCoord ? 'middle' : (offX > 0 ? 'start' : offX < 0 ? 'end' : 'middle')
        }

        g.append('text')
          .attr('class', 'point-label')
          .attr('x', cx + offX)
          .attr('y', cy + offY)
          .attr('text-anchor', anchor)
          .style('fill', s.fontColor || s.labelColor || null)
          .style('font-size', s.fontSize ? `${s.fontSize}px` : (s.size ? `${s.size}px` : null))
          .style('font-weight', s.fontWeight || s.labelWeight || null)
          .style('font-style', s.fontStyle || (s.isSea ? 'italic' : null))
          .style('stroke', s.textStroke || null)
          .style('stroke-width', s.textStrokeWidth ? `${s.textStrokeWidth}px` : null)
          .text(p.name)
      })
    }
  }

  // ============================================================
  // ⭐ redrawScenePaths:用「指定的 bbox」重畫當前 scene 的所有 path
  // 給 flyToBbox 跟 interpolateBbox 共用
  //
  // [v13] 改為「整層砍掉重畫」策略 (drawLayer 內部用 append 沒做 data
  // binding,所以無法 selectAll().data() 更新位置)。砍掉重畫對效能影響
  // 微小 (每個 scene 幾十個 features),換來程式碼一致 + 新舊格式統一。
  // ============================================================
  function redrawScenePaths(sceneId, bbox) {
    const state = sceneStates[sceneId]
    if (!state || !state.built) return

    const projection = createProjectionForBbox(bbox, width, height, 1.0)
    const path = d3.geoPath(projection)
    state.projection = projection
    state.currentBbox = bbox

    const contentG = state.g.select('.content-g')

    // 重畫 land
    contentG.select('.land').attr('d', path)

    // 重畫所有 layer:砍掉原來的 layer-xxx group, 重新 drawLayer
    // [v13.1] opacity 來源優先順序:
    //   1. alwaysVisible → 1
    //   2. sceneState.visibleLayers 包含 layer.id → 1 (step toggle on)
    //   3. 現存 layer DOM 的 opacity (相容鏡頭飛行進行中的中間狀態)
    //   4. fallback 0
    // 這樣可確保鏡頭飛行期間,layer 被砍掉重畫後仍維持「該顯示」狀態,
    // step layer 不會等飛行結束才出現。
    if (state.scene.layers) {
      const visibleSet = state.visibleLayers || new Set()
      for (const layer of state.scene.layers) {
        const features = state.layerFeatures[layer.id] || []
        const oldLayer = contentG.select(`.layer-${layer.id}`)
        let opacity
        if (layer.alwaysVisible) {
          opacity = 1
        } else if (visibleSet.has(layer.id)) {
          opacity = 1
        } else if (!oldLayer.empty()) {
          const op = oldLayer.style('opacity')
          opacity = (op != null && op !== '') ? parseFloat(op) : 0
        } else {
          opacity = 0
        }
        oldLayer.remove()
        drawLayer(contentG, layer, features, path, projection, opacity)
      }
    }
  }

  // ============================================================
  // ⭐⭐⭐ 四個對外 API ⭐⭐⭐
  // ============================================================

  let activeScene = null

  /**
   * setActiveStraitId(id) — 切換 scene
   * 多次連呼同一 id 會被守門擋掉,不重複動畫
   * step 4 instant: 瞬間切換(不 fade)
   *   理由:所有 setActiveStraitId 都在遮罩(黑幕或白遮罩)底下發生,
   *   讀者看不到 fade 動畫,留著 fade 反而會讓「黑幕揭幕」時新場景還沒就位。
   *   瞬間切確保揭幕當下新場景完全顯示。
   */
  async function setActiveStraitId(sceneId) {
    if (sceneId === activeScene) return
    await buildScene(sceneId) // 第一次切到才會 build,有快取
    activeScene = sceneId
    currentSceneId.set(sceneId)
    // 瞬間切換:直接設 opacity,不用 transition
    svg.selectAll('.scene-group').style('opacity', 0)
    svg.select(`#scene-${sceneId}`).style('opacity', 1)
  }

  /**
   * setVisibleLayers(layerIds) — 切 layer 顯示組合(沿用 v9 機制)
   * 也順便更新 legend 的 reactive 狀態
   */
  function setVisibleLayers(layerIds) {
    if (!activeScene) return
    const scene = straitScenes[activeScene]
    if (!scene || !scene.layers) return

    const visible = new Set(layerIds || [])

    // [v13.1] 記錄到 sceneState,讓 redrawScenePaths 重建 layer 時知道
    //         「這個 layer 該顯示」,即使 flyToBbox 砍掉 layer DOM 也能維持狀態
    const state = sceneStates[activeScene]
    if (state) state.visibleLayers = visible

    scene.layers.forEach(layer => {
      // alwaysVisible 不受 step 切換影響,永遠保持 1
      if (layer.alwaysVisible) return
      const shouldShow = visible.has(layer.id)
      // [v13.1] 直接設 opacity (不用 d3 transition),由 CSS .layer 的
      //         transition: opacity 0.5s ease 接管。優點:鏡頭飛行期間
      //         layer 被砍掉重畫,CSS transition 銜接更自然。
      svg.select(`#scene-${activeScene} .layer-${layer.id}`)
        .style('opacity', shouldShow ? 1 : 0)
    })

    // 更新 legend(排除 alwaysVisible)
    legendLayers = (scene.layers || []).filter(l => !l.alwaysVisible)
    legendVisibleSet = visible
  }

  /**
   * flyToBbox(bbox) — 鏡頭動畫到指定 bbox(D3 transition,scene 內部變焦)
   * 不切 scene、只動 projection。對 bbox 四個座標做線性插值,每幀重畫 path。
   * 連呼會中斷上一個 transition(d3 預設行為)。
   */
  function flyToBbox(bbox) {
    if (!activeScene || !bbox) return
    const state = sceneStates[activeScene]
    if (!state || !state.built) return

    const fromBbox = state.currentBbox
    if (!fromBbox) {
      redrawScenePaths(activeScene, bbox)
      return
    }
    // 已經在這個 bbox,不動畫
    if (
      fromBbox[0][0] === bbox[0][0] && fromBbox[0][1] === bbox[0][1] &&
      fromBbox[1][0] === bbox[1][0] && fromBbox[1][1] === bbox[1][1]
    ) return

    // 用一個 dummy selection 跑 transition,每幀對 bbox 線性插值、重畫
    d3.select({})
      .transition()
      .duration(FLY_DURATION_MS)
      .ease(d3.easeCubicInOut)
      .tween(`fly-${activeScene}`, () => {
        const interp = d3.interpolateArray(
          [fromBbox[0][0], fromBbox[0][1], fromBbox[1][0], fromBbox[1][1]],
          [bbox[0][0], bbox[0][1], bbox[1][0], bbox[1][1]]
        )
        return (t) => {
          const v = interp(t)
          redrawScenePaths(activeScene, [[v[0], v[1]], [v[2], v[3]]])
        }
      })
  }

  /**
   * interpolateBbox(fromBbox, toBbox, progress) — 跟著外部 progress 連續變焦
   * 給 path_transition.onUpdate 用。沒有 D3 transition,直接吃 0~1 的 progress。
   */
  function interpolateBbox(fromBbox, toBbox, progress) {
    if (!activeScene || !fromBbox || !toBbox) return
    const t = Math.max(0, Math.min(1, progress))
    const lerp = (a, b) => a + (b - a) * t
    const interpBbox = [
      [lerp(fromBbox[0][0], toBbox[0][0]), lerp(fromBbox[0][1], toBbox[0][1])],
      [lerp(fromBbox[1][0], toBbox[1][0]), lerp(fromBbox[1][1], toBbox[1][1])]
    ]
    redrawScenePaths(activeScene, interpBbox)
  }

  // ============================================================
  // Legend reactive state
  // ============================================================
  let legendLayers = []
  let legendVisibleSet = new Set()
  // [v13] showLegend 邏輯:
  //   legendLayers 已排除 alwaysVisible (在 setVisibleLayers 內過濾)
  //   只要 legendLayers 裡至少有一個 layer 是 visible 才顯示 legend
  //   (避免「visibleSet 只有 alwaysVisible 的 id」時 legend 空但仍顯示)
  $: showLegend = legendLayers.some(l => legendVisibleSet.has(l.id))
</script>

<style>
  svg { width: 100%; height: 100vh; display: block; background: var(--color-sea, #ffffff); }
  .strait-map-wrap { position: relative; overflow: hidden; }
  :global(.sea-bg) { fill: var(--color-sea, #ffffff); }
  :global(.land) {
    fill: var(--color-land, #dcd7cd);
    stroke: var(--color-border, #ffffff);
    stroke-width: 0.6px;
  }
  :global(.layer) { transition: opacity 0.5s ease; }
  :global(.point-label) {
    fill: var(--color-text-dim, #777777); font-size: 12px;
    font-family: 'Noto Sans TC', sans-serif;
    pointer-events: none;
    paint-order: stroke; stroke: var(--color-bg, #f3f1ec); stroke-width: 3px; stroke-linejoin: round;
  }
  :global(.scene-title) {
    fill: var(--color-text-dim, #777777); font-size: 14px;
    font-family: 'Noto Sans TC', sans-serif;
    letter-spacing: 0.2em; text-anchor: middle; pointer-events: none;
  }

  /* ⭐ 白遮罩 — 蓋在地圖上,蓋板文字在它上面 */
  #full-box-bg {
    position: absolute;
    width: 100vw; max-width: 100%;
    height: 100vh;
    top: 0; left: 0;
    background-color: var(--color-bg-paper, #ffffff);
    transition: opacity 0.3s ease-out;
    pointer-events: none;
    z-index: 11;
    opacity: 0;
  }

  /* ⭐ 過渡幕(海峽 → 海峽 之間用),路透淺色版改成深炭色 */
  #intro-curtain {
    position: absolute;
    width: 100vw; max-width: 100%;
    height: 100vh;
    top: 0; left: 0;
    background-color: var(--color-curtain, #222222);
    transition: opacity 0.6s ease-out;
    pointer-events: none;
    z-index: 10;
    opacity: 0;
  }

  /* Legend(若有用到) */
  .legend {
    position: absolute; top: 80px; left: 24px;
    background: rgba(255, 255, 255, 0.92);
    padding: 0.8rem 1rem; font-size: 0.8rem; color: var(--color-text, #222222);
    pointer-events: none; border-left: 2px solid var(--color-accent, #d44545);
    min-width: 140px; transition: opacity 0.4s ease;
    z-index: 3;
    opacity: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
  .legend.visible { opacity: 1; }
  .legend-title {
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--color-text-dim, #777777);
    letter-spacing: 0.12em;
    margin-bottom: 0.5rem;
    padding-bottom: 0.4rem;
    border-bottom: 1px solid var(--color-border-tertiary, rgba(0,0,0,0.08));
  }
  .legend-row {
    display: flex; align-items: center; gap: 0.6rem;
    margin-bottom: 0.35rem;
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  .legend-row.is-active { opacity: 1; }
  .legend-row:last-child { margin-bottom: 0; }
  .legend-swatch { width: 18px; height: 3px; flex-shrink: 0; }
  .legend-swatch.line { width: 18px; height: 3px; }
  .legend-swatch.point { width: 10px; height: 10px; border-radius: 50%; }
  .legend-swatch.diamond {
    width: 10px; height: 10px;
    transform: rotate(45deg);
  }
  .legend-swatch.triangle {
    width: 0; height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 8px solid;
  }
  .legend-swatch.polygon { width: 18px; height: 10px; }
  .legend-swatch.dashed {
    height: 3px;
    background-size: 6px 3px;
    background-repeat: repeat-x;
    background-image: linear-gradient(to right, currentColor 60%, transparent 40%);
  }
</style>

<div class="strait-map-wrap" style="width:{width}px; position: relative;">
  <svg id="strait-map" {width} {height} viewBox="0 0 {width} {height}" />

  <!-- ⭐ 決策 B:白遮罩,寫死在這 -->
  <div id="full-box-bg"></div>

  <!-- ⭐ step 3d:intro 揭幕黑布(深色 #0f1419,跟 body 背景一致) -->
  <div id="intro-curtain"></div>

  <!-- 左上角圖例 -->
  <!--
    [v13] legend 邏輯更新:
      - 加標題「圖示說明」
      - row 文字優先讀 layer.legendLabel (中文),fallback 到 layer.id
      - swatch 樣式從 preset 讀取,支援新格式
        (舊格式 layer.style 仍能 fallback)
  -->
  <div class="legend" class:visible={showLegend}>
    <div class="legend-title">圖示說明</div>
    {#each legendLayers as layer (layer.id)}
      {@const isActive = legendVisibleSet.has(layer.id)}
      {@const s = (layer.preset ? MAP_PRESETS[layer.preset] : null) || layer.style || {}}
      {@const t = s.type || layer.type}
      <div class="legend-row" class:is-active={isActive}>
        {#if t === 'point' && s.shape === 'diamond'}
          <div class="legend-swatch diamond" style="background: {s.fill || '#fff'}"></div>
        {:else if t === 'point' && (s.shape === 'triangle' || s.symbol === 'triangle')}
          <div class="legend-swatch triangle" style="border-bottom-color: {s.fill || '#fff'}"></div>
        {:else if t === 'point'}
          <div class="legend-swatch point"
               style="background: {s.fill === 'transparent' ? 'transparent' : (s.fill || '#fff')};
                      border: {s.stroke && s.stroke !== 'none' ? `1.5px solid ${s.stroke}` : 'none'};"></div>
        {:else if t === 'polygon'}
          <div class="legend-swatch polygon"
               style="background: {s.fill || 'transparent'}; opacity: {s.fillOpacity ?? 1}; border: 1px solid {s.stroke || s.fill || '#fff'};"></div>
        {:else if s.strokeDasharray}
          <div class="legend-swatch dashed" style="color: {s.stroke || '#fff'}"></div>
        {:else}
          <div class="legend-swatch line" style="background: {s.stroke || s.fill || '#fff'}"></div>
        {/if}
        <span>{layer.legendLabel || layer.label || layer.id}</span>
      </div>
    {/each}
  </div>
</div>
